import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { useDisclosure } from '@mantine/hooks'

import { onMutationCompleted } from 'src/lib/mutationEvents'

const ApplicantDrawerContext = createContext(null)

const ApplicantDrawerContextProvider = ({ children }) => {
  const [opened, { open, close }] = useDisclosure(false)
  const [applicantId, setApplicantId] = useState(null)
  const [refreshKey, setRefreshKey] = useState(0)

  // While the drawer is open, changes made in it are collected and applied
  // to lists keyed on refreshKey once, when it closes.
  const openedRef = useRef(opened)
  openedRef.current = opened
  const pendingRefreshRef = useRef(false)

  useEffect(
    () =>
      onMutationCompleted(() => {
        if (openedRef.current) pendingRefreshRef.current = true
      }),
    []
  )

  const handleOpen = (id) => {
    setApplicantId(id)
    open()
  }

  const triggerRefresh = useCallback(() => {
    if (openedRef.current) {
      pendingRefreshRef.current = true
    } else {
      setRefreshKey((prev) => prev + 1)
    }
  }, [])

  const handleClose = useCallback(() => {
    close()
    openedRef.current = false
    if (pendingRefreshRef.current) {
      pendingRefreshRef.current = false
      setRefreshKey((prev) => prev + 1)
    }
  }, [close])

  return (
    <ApplicantDrawerContext.Provider
      value={{
        opened,
        close: handleClose,
        handleOpen,
        applicantId,
        refreshKey,
        triggerRefresh,
      }}
    >
      {children}
    </ApplicantDrawerContext.Provider>
  )
}

export function useApplicantDrawerContext() {
  return useContext(ApplicantDrawerContext)
}

export { ApplicantDrawerContext, ApplicantDrawerContextProvider }
